import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"
import { toast } from "../services/toast"

// Scanner controller for QR codes and barcodes
// Supports both QR codes and common barcode formats (EAN-13, Code128, etc.)
export default class extends Controller {
  static targets = ["video", "result", "startButton", "stopButton", "overlay", "container", "toggleButton", "buttonText", "permissionMessage"]
  static values = {
    scanMode: { type: String, default: "both" }, // "qr", "barcode", "both"
    autoStop: { type: Boolean, default: true },
    searchUrl: { type: String, default: "" }
  }

  connect() {
    this.scanner = null
    this.isScanning = false
    this.scannerId = `scanner-video`
    this.permissionGranted = false
    this.permissionChecked = false
    
    // Check initial camera permission state
    this.checkCameraPermission()
  }

  disconnect() {
    this.stopScanning()
  }

  async checkCameraPermission() {
    try {
      const result = await navigator.permissions.query({ name: 'camera' })
      this.permissionGranted = result.state === 'granted'
      this.permissionChecked = true
      
      // Listen for permission changes (user might change in browser settings)
      result.addEventListener('change', () => {
        this.permissionGranted = result.state === 'granted'
      })
    } catch (err) {
      // Permissions API not supported (older browsers), we'll check on first use
      this.permissionChecked = false
    }
  }

  async toggleWithPermission(event) {
    event?.preventDefault()
    
    if (this.isScanning) {
      await this.stop()
      return
    }

    // If permission already granted, start directly without requesting again
    if (this.permissionGranted) {
      await this.start()
      return
    }

    // If we haven't checked permissions yet (API not available), try starting directly
    // The browser will prompt if needed
    if (!this.permissionChecked) {
      await this.start()
      return
    }

    // Permission not granted and we know it - request explicitly
    try {
      // Request camera access to trigger permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      // Permission granted, stop the test stream
      stream.getTracks().forEach(track => track.stop())
      this.permissionGranted = true
      // Now start the scanner
      await this.start()
    } catch (err) {
      console.error('Camera permission denied:', err)
      this.showPermissionMessage()
    }
  }

  async requestPermission(event) {
    event?.preventDefault()
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      stream.getTracks().forEach(track => track.stop())
      this.permissionGranted = true
      this.hidePermissionMessage()
      await this.start()
    } catch (err) {
      console.error('Camera permission denied:', err)
      this.showError('No se pudo acceder a la cámara. Por favor, permite el acceso en la configuración de tu navegador.')
    }
  }

  showPermissionMessage() {
    if (this.hasPermissionMessageTarget) {
      this.permissionMessageTarget.classList.remove('hidden')
    }
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add('hidden')
    }
  }

  hidePermissionMessage() {
    if (this.hasPermissionMessageTarget) {
      this.permissionMessageTarget.classList.add('hidden')
    }
  }

  async start(event) {
    event?.preventDefault()
    
    if (this.isScanning) return

    try {
      // Show container
      if (this.hasContainerTarget) {
        this.containerTarget.classList.remove('hidden')
      }

      this.scanner = new Html5Qrcode(this.scannerId)
      
      const config = {
        fps: 10,
        qrbox: this.getQrBoxSize(),
        aspectRatio: 1.0,
        formatsToSupport: this.getFormatsToSupport()
      }

      await this.scanner.start(
        { facingMode: "environment" }, // Use back camera
        config,
        (decodedText, decodedResult) => this.onCodeDetected(decodedText, decodedResult),
        (errorMessage) => {
          // Ignore errors during scanning (expected when no code in frame)
        }
      )

      this.isScanning = true
      this.updateUI(true)

    } catch (err) {
      console.error("Error starting scanner:", err)
      if (this.hasContainerTarget) {
        this.containerTarget.classList.add('hidden')
      }
      
      if (err.name === 'NotAllowedError') {
        this.showPermissionMessage()
      } else {
        this.showError("No se pudo acceder a la cámara. Verifica los permisos.")
      }
    }
  }

  async stop(event) {
    event?.preventDefault()
    await this.stopScanning()
  }

  async stopScanning() {
    if (this.scanner && this.isScanning) {
      try {
        await this.scanner.stop()
        this.scanner.clear()
      } catch (err) {
        console.error("Error stopping scanner:", err)
      }
    }
    
    this.isScanning = false
    this.updateUI(false)
    
    // Hide container
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add('hidden')
    }
  }

  async toggle(event) {
    event?.preventDefault()
    
    if (this.isScanning) {
      await this.stop()
    } else {
      await this.start()
    }
  }

  onCodeDetected(decodedText, decodedResult) {
    const format = decodedResult?.result?.format?.formatName || "UNKNOWN"
    
    // Play success sound/vibration
    this.playSuccessFeedback()
    
    // Update result display if target exists
    if (this.hasResultTarget) {
      this.resultTarget.textContent = decodedText
      this.resultTarget.dataset.format = format
    }

    // Dispatch custom event for other controllers to listen
    this.dispatch("detected", {
      detail: {
        code: decodedText,
        format: format,
        timestamp: new Date().toISOString()
      }
    })

    // Auto-stop if configured
    if (this.autoStopValue) {
      this.stopScanning()
    }

    // If search URL is configured, redirect or search
    if (this.searchUrlValue) {
      this.performSearch(decodedText)
    }
  }

  performSearch(code) {
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("code", code)
    
    // Use Turbo for navigation
    Turbo.visit(url.toString())
  }

  getFormatsToSupport() {
    const Html5QrcodeSupportedFormats = Html5Qrcode.getSupportedFormats ? 
      Html5Qrcode.getSupportedFormats() : []

    switch (this.scanModeValue) {
      case "qr":
        return [0] // QR_CODE = 0
      case "barcode":
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13] // All barcode formats
      case "both":
      default:
        return undefined // All formats
    }
  }

  getQrBoxSize() {
    // Responsive QR box size based on viewport
    const minDimension = Math.min(window.innerWidth, window.innerHeight)
    const size = Math.min(280, minDimension * 0.7)
    return { width: size, height: size }
  }

  updateUI(isScanning) {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.style.display = isScanning ? "none" : ""
    }
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.style.display = isScanning ? "" : "none"
    }
    if (this.hasToggleButtonTarget) {
      if (this.hasButtonTextTarget) {
        this.buttonTextTarget.textContent = isScanning ? 'Detener escáner' : 'Escanear código'
      }
      // Update button icon if needed
      const icon = this.toggleButtonTarget.querySelector('i')
      if (icon) {
        icon.className = isScanning ? 'bi bi-stop-circle' : 'bi bi-qr-code-scan'
      }
    }
  }

  showOverlay(show) {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.toggle("active", show)
    }
  }

  playSuccessFeedback() {
    // Vibrate if supported (mobile devices)
    if (navigator.vibrate) {
      navigator.vibrate(100)
    }

    // Play sound if available
    const audio = document.querySelector("[data-notification-target='audio']")
    if (audio) {
      audio.currentTime = 0
      audio.play().catch(() => {})
    }
  }

  showError(message) {
    // Dispatch error event
    this.dispatch("error", { detail: { message } })
    
    // Show toast notification using Notyf
    toast.error(message)
  }

  // Public method to get last scanned code
  getLastCode() {
    return this.hasResultTarget ? this.resultTarget.textContent : null
  }
}
